import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Lounges.css";

const FILTER_TABS = ["All", "Europe", "Americas", "Asia", "Middle East"];

const LOUNGES = [
  { id: 1, name: "Dan Lounge", code: "TLV", airportName: "Ben Gurion", terminal: "Terminal 3", tagline: "Israeli cuisine & cocktail bar", rating: 4.2, hours: "24h", seats: 150, amenities: ["Food", "Bar", "WiFi", "Shower"], location: "Departures, after security, Gate B12–B24", region: "Middle East" },
  { id: 2, name: "Fattal Lounge", code: "TLV", airportName: "Ben Gurion", terminal: "Terminal 3", tagline: "Fresh pastries & quiet workspace", rating: 4.0, hours: "05–23", seats: 80, amenities: ["Food", "WiFi"], location: "Departures, near Gate A3", region: "Middle East" },
  { id: 3, name: "El Al King David", code: "TLV", airportName: "Ben Gurion", terminal: "Terminal 3", tagline: "Premium bar, gourmet dining & showers", rating: 4.5, hours: "06–22", seats: 200, amenities: ["Food", "Bar", "WiFi", "Shower", "Press"], location: "Departures, after passport control", region: "Middle East" },
  { id: 4, name: "Galleries First", code: "LHR", airportName: "Heathrow", terminal: "Terminal 5", tagline: "BA's flagship lounge with à la carte dining", rating: 4.6, hours: "05–22", seats: 340, amenities: ["Food", "Bar", "WiFi", "Shower", "Spa"], location: "Terminal 5, after security", region: "Europe" },
  { id: 5, name: "Star Alliance Lounge", code: "CDG", airportName: "Charles de Gaulle", terminal: "Terminal 1", tagline: "Runway views & French cuisine", rating: 4.3, hours: "06–23", seats: 260, amenities: ["Food", "Bar", "WiFi", "Shower"], location: "Terminal 1, Hall L", region: "Europe" },
  { id: 6, name: "Lufthansa Senator", code: "FRA", airportName: "Frankfurt", terminal: "Terminal 1", tagline: "Premium German hospitality", rating: 4.4, hours: "05:30–22", seats: 180, amenities: ["Food", "Bar", "WiFi", "Shower", "Rest"], location: "Terminal 1, Concourse B", region: "Europe" },
  { id: 7, name: "Centurion Lounge", code: "JFK", airportName: "JFK New York", terminal: "Terminal 4", tagline: "Upscale cocktails & chef-crafted dishes", rating: 4.5, hours: "06–23", seats: 220, amenities: ["Food", "Bar", "WiFi", "Shower"], location: "Terminal 4, after security", region: "Americas" },
  { id: 8, name: "United Polaris", code: "EWR", airportName: "Newark", terminal: "Terminal C", tagline: "Lie-flat rest pods & premium dining", rating: 4.7, hours: "05:30–22", seats: 190, amenities: ["Food", "Bar", "WiFi", "Shower", "Rest"], location: "Terminal C, Gate C120", region: "Americas" },
  { id: 9, name: "SilverKris Lounge", code: "SIN", airportName: "Changi", terminal: "Terminal 3", tagline: "Singapore Airlines' iconic experience", rating: 4.6, hours: "24h", seats: 300, amenities: ["Food", "Bar", "WiFi", "Shower", "Rest"], location: "Terminal 3, Transit Level 3", region: "Asia" },
  { id: 10, name: "Cathay Pacific Lounge", code: "HKG", airportName: "Hong Kong", terminal: "Terminal 1", tagline: "Noodle bar, showers & serene atmosphere", rating: 4.4, hours: "05:30–00:30", seats: 250, amenities: ["Food", "Bar", "WiFi", "Shower"], location: "Terminal 1, near Gate 63", region: "Asia" },
  { id: 11, name: "Emirates First", code: "DXB", airportName: "Dubai", terminal: "Concourse A", tagline: "Luxury spa, cigar bar & fine dining", rating: 4.8, hours: "24h", seats: 400, amenities: ["Food", "Bar", "WiFi", "Shower", "Spa"], location: "Concourse A, after security", region: "Middle East" },
];

const NEARBY_LOUNGES = [
  { id: 3, name: "El Al King David", terminal: "T3", rating: 4.5, dist: "~8 min walk", open: true },
  { id: 1, name: "Dan Lounge", terminal: "T3", rating: 4.2, dist: "~5 min walk", open: true },
  { id: 2, name: "Fattal Lounge", terminal: "T3", rating: 4.0, dist: "~10 min walk", open: true },
];

const VISIT_HISTORY = [
  { name: "El Al King David", airport: "Ben Gurion (TLV)", date: "Mar 15, 2026" },
  { name: "Emirates First", airport: "Dubai (DXB)", date: "Feb 28, 2026" },
  { name: "Galleries First", airport: "Heathrow (LHR)", date: "Feb 12, 2026" },
  { name: "SilverKris Lounge", airport: "Singapore (SIN)", date: "Jan 8, 2026" },
];

function getStoredPasses() {
  try {
    const raw = localStorage.getItem("visa-lounge-passes");
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function isPassValid(pass) {
  const today = new Date().toDateString();
  return new Date(pass.generatedAt).toDateString() === today;
}

/* ── Home view (new v3 design) ── */
function LoungeHome({ onBrowse, onMenu, navigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = () => {
    if (searchQuery.trim()) onBrowse(searchQuery.trim());
  };

  const MENU_ITEMS = [
    {
      id: "history",
      label: "Visit History",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
    },
    {
      id: "passes",
      label: "Active Passes",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      ),
    },
    {
      id: "benefit-info",
      label: "Benefit Information",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      ),
    },
  ];

  return (
    <>
      {/* Header */}
      <header className="la-header">
        <Link to="/benefits" className="la-back">← Benefits</Link>
        <h1 className="la-title">Lounge Access</h1>
        <button className="la-menu-btn" onClick={() => setMenuOpen(true)} type="button" aria-label="Menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6fdc8c" strokeWidth="2.2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
      </header>

      {/* Contextual banner */}
      <div className="la-context-banner">
        <span className="la-pulse-dot" />
        <span className="la-context-text">Ben Gurion Airport — 3 lounges available nearby</span>
      </div>

      {/* Search module */}
      <div className="la-search-section">
        <span className="la-section-label">FIND A LOUNGE</span>
        <div className="la-search-row">
          <input type="text" className="la-search-input" placeholder="Search airport or city..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
          <button className="la-search-go" onClick={handleSearch} type="button">Go</button>
        </div>
        <button className="la-browse-link" onClick={() => onBrowse("")} type="button">
          Browse all airports ›
        </button>
      </div>

      {/* Nearby */}
      <div className="la-nearby-section">
        <span className="la-section-label">📍 NEARBY</span>
        {NEARBY_LOUNGES.map((l) => (
          <button key={l.id} className="la-nearby-row" onClick={() => navigate(`/lounges/${l.id}`)} type="button">
            <div className="la-nearby-info">
              <div className="la-nearby-top">
                <span className="la-nearby-name">{l.name}</span>
                <span className="la-nearby-rating">★ {l.rating}</span>
              </div>
              <div className="la-nearby-meta">
                <span>{l.terminal}</span><span>·</span><span>{l.dist}</span>
                {l.open && <span className="la-open-badge">Open now</span>}
              </div>
            </div>
            <span className="la-nearby-chevron">›</span>
          </button>
        ))}
      </div>

      {/* Benefit strip — pinned bottom */}
      <div className="la-benefit-strip">
        <div className="la-strip-info">
          <span className="la-strip-main">Unlimited visits this year</span>
          <span className="la-strip-sub">+1 guest per visit · ◆ Diamond tier</span>
        </div>
        <span className="la-strip-badge">Active</span>
      </div>

      <footer className="powered-by" style={{ padding: "24px 0 100px" }}>Powered by Visa</footer>

      {/* Menu — 3 items only */}
      {menuOpen && (
        <>
          <div className="la-menu-overlay" onClick={() => setMenuOpen(false)} />
          <div className="la-menu-sheet">
            <div className="la-menu-handle" />
            <div className="la-menu-items-list">
              {MENU_ITEMS.map((item) => (
                <button
                  key={item.id}
                  className="la-menu-nav-row"
                  type="button"
                  onClick={() => { setMenuOpen(false); onMenu(item.id); }}
                >
                  <span className="la-menu-nav-icon">{item.icon}</span>
                  <span className="la-menu-nav-label">{item.label}</span>
                  <svg className="la-menu-nav-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ── History Page ── */
function LoungeHistory({ onBack }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? VISIT_HISTORY : VISIT_HISTORY.slice(0, 5);
  return (
    <div className="la-subpage">
      <header className="la-subpage-header">
        <button className="la-subpage-back" onClick={onBack} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1 className="la-subpage-title">Visit History</h1>
      </header>
      <div className="la-subpage-body">
        {visible.map((v, i) => (
          <div key={i} className="la-history-row">
            <div className="la-history-info">
              <span className="la-history-name">{v.name}</span>
              <span className="la-history-airport">{v.airport}</span>
            </div>
            <span className="la-history-date">{v.date}</span>
          </div>
        ))}
        {!showAll && VISIT_HISTORY.length > 5 && (
          <button className="la-show-more" onClick={() => setShowAll(true)} type="button">
            Show {VISIT_HISTORY.length - 5} more ›
          </button>
        )}
        {VISIT_HISTORY.length === 0 && (
          <p className="la-empty-state">No visits recorded yet.</p>
        )}
      </div>
    </div>
  );
}

/* ── Active Passes Page ── */
function LoungePasses({ onBack }) {
  const [passes] = useState(getStoredPasses);
  return (
    <div className="la-subpage">
      <header className="la-subpage-header">
        <button className="la-subpage-back" onClick={onBack} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1 className="la-subpage-title">Active Passes</h1>
      </header>
      <div className="la-subpage-body">
        {passes.length === 0 ? (
          <p className="la-empty-state" style={{ paddingTop: 32 }}>No passes generated yet.<br/>Visit a lounge and generate a pass to see it here.</p>
        ) : (
          passes.map((p, i) => {
            const valid = isPassValid(p);
            return (
              <div key={i} className="la-pass-row">
                <div className="la-pass-info">
                  <span className="la-pass-name">{p.loungeName}</span>
                  <span className="la-pass-date">{new Date(p.generatedAt).toLocaleDateString()}</span>
                </div>
                <span className={`la-pass-status ${valid ? "valid" : "expired"}`}>
                  {valid ? "Valid" : "Expired"}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ── Benefit Information Page ── */
function LoungeBenefitInfo({ onBack }) {
  const INFO_ROWS = [
    { icon: "✓", text: "Unlimited lounge visits per year" },
    { icon: "✓", text: "+1 guest per visit at no charge" },
    { icon: "✓", text: "Access to 1,300+ lounges in 180+ countries" },
    { icon: "✓", text: "Exclusive to ◆ Diamond tier members" },
    { icon: "✓", text: "QR pass generated just-in-time at the lounge" },
    { icon: "✓", text: "Valid same-day — no pre-booking required" },
  ];
  return (
    <div className="la-subpage">
      <header className="la-subpage-header">
        <button className="la-subpage-back" onClick={onBack} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1 className="la-subpage-title">Benefit Information</h1>
      </header>
      <div className="la-subpage-body">
        <div className="la-binfo-hero">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6fdc8c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21 4 19 2c-2-2-4-2-5.5-.5L10 5 1.8 6.2l4.2 4.2-1.5 5.5 4.2-1.5z"/>
          </svg>
          <div>
            <div className="la-binfo-hero-title">Visa Priority Pass</div>
            <div className="la-binfo-hero-sub">◆ Diamond tier benefit</div>
          </div>
        </div>
        <div className="la-binfo-list">
          {INFO_ROWS.map((r, i) => (
            <div key={i} className="la-binfo-row">
              <span className="la-binfo-check">{r.icon}</span>
              <span className="la-binfo-text">{r.text}</span>
            </div>
          ))}
        </div>
        <div className="la-binfo-footer">Powered by Visa Priority Pass</div>
      </div>
    </div>
  );
}

/* ── Browse view (existing airport grid) ── */
function LoungeBrowse({ initialSearch, onBack }) {
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState(initialSearch || "");
  const [expandedAirport, setExpandedAirport] = useState(null);
  const [drawerLounge, setDrawerLounge] = useState(null);

  function handleFilterChange(tab) {
    setActiveFilter(tab);
    setExpandedAirport(null);
    setDrawerLounge(null);
  }

  const filtered = useMemo(() => {
    return LOUNGES.filter((l) => {
      if (activeFilter !== "All" && l.region !== activeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return l.name.toLowerCase().includes(q) || l.code.toLowerCase().includes(q) || l.airportName.toLowerCase().includes(q);
      }
      return true;
    });
  }, [activeFilter, searchQuery]);

  const airports = useMemo(() => {
    const map = {};
    filtered.forEach((l) => {
      if (!map[l.code]) {
        map[l.code] = { code: l.code, name: l.airportName, region: l.region, lounges: [] };
      }
      map[l.code].lounges.push(l);
    });
    return Object.values(map);
  }, [filtered]);

  function toggleAirport(code) {
    setExpandedAirport((prev) => (prev === code ? null : code));
    setDrawerLounge(null);
  }

  return (
    <>
      <button className="back-link" onClick={onBack} type="button">← Lounge Home</button>

      <section className="lounges-hero">
        <h1 className="lounges-title">Browse Airports</h1>
        <p className="lounges-subtitle">1,300+ lounges · 450 airports</p>
      </section>

      <div className="lounges-search">
        <input
          type="text"
          placeholder="Search airport or lounge"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setExpandedAirport(null); setDrawerLounge(null); }}
          className="lounges-search-input"
        />
      </div>

      <div className="lounges-filters">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            className={`filter-tab ${activeFilter === tab ? "active" : ""}`}
            onClick={() => handleFilterChange(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className={`airport-grid ${expandedAirport ? "has-expanded" : ""}`}>
        {airports.filter((ap) => !expandedAirport || ap.code === expandedAirport).map((ap) => {
          const isOpen = expandedAirport === ap.code;
          return (
            <div key={ap.code} className={`airport-card ${isOpen ? "expanded" : ""}`}>
              <button
                className="airport-summary"
                onClick={() => toggleAirport(ap.code)}
                type="button"
              >
                {isOpen && <span className="airport-back">←</span>}
                <span className="airport-code">{ap.code}</span>
                <span className="airport-name">{ap.name}</span>
                <span className="airport-count">
                  {ap.lounges.length} {ap.lounges.length === 1 ? "lounge" : "lounges"}
                </span>
              </button>

              {isOpen && (
                <div className="airport-lounges">
                  {ap.lounges.map((lounge) => (
                    <button
                      key={lounge.id}
                      className="lounge-row"
                      onClick={() => setDrawerLounge(lounge)}
                      type="button"
                    >
                      <div className="lounge-main">
                        <div className="lounge-top">
                          <span className="lounge-name">{lounge.name}</span>
                          <span className="lounge-rating">★ {lounge.rating}</span>
                        </div>
                        <span className="lounge-tagline">{lounge.tagline}</span>
                        <div className="lounge-meta">
                          <span>{lounge.terminal}</span>
                          <span>·</span>
                          <span>{lounge.hours}</span>
                          <span>·</span>
                          <span>{lounge.seats} seats</span>
                        </div>
                      </div>
                      <span className="lounge-chevron">›</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {airports.length === 0 && (
        <p className="lounges-empty">No airports found for this filter.</p>
      )}

      <footer className="powered-by">Powered by Visa</footer>

      {drawerLounge && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawerLounge(null)} />
          <div className="drawer">
            <div className="drawer-handle" />
            <div className="drawer-header">
              <div>
                <h2 className="drawer-title">{drawerLounge.name}</h2>
                <p className="drawer-sub">{drawerLounge.airportName} · {drawerLounge.terminal}</p>
              </div>
              <button className="drawer-close" onClick={() => setDrawerLounge(null)} type="button">×</button>
            </div>

            <div className="drawer-stats">
              <div className="drawer-stat">
                <span className="drawer-stat-value">★ {drawerLounge.rating}</span>
                <span className="drawer-stat-label">Rating</span>
              </div>
              <div className="drawer-stat">
                <span className="drawer-stat-value">{drawerLounge.hours}</span>
                <span className="drawer-stat-label">Hours</span>
              </div>
              <div className="drawer-stat">
                <span className="drawer-stat-value">{drawerLounge.seats}</span>
                <span className="drawer-stat-label">Seats</span>
              </div>
            </div>

            <p className="drawer-tagline">{drawerLounge.tagline}</p>

            <div className="drawer-amenities">
              {drawerLounge.amenities.map((a, i) => (
                <span key={i} className="amenity-pill">{a}</span>
              ))}
            </div>

            <p className="drawer-location">{drawerLounge.location}</p>

            <div className="drawer-actions">
              <Link to={`/lounges/${drawerLounge.id}`} className="drawer-btn outline" onClick={() => setDrawerLounge(null)}>
                Full details
              </Link>
              <Link to={`/lounges/${drawerLounge.id}/pass`} className="drawer-btn primary" onClick={() => setDrawerLounge(null)}>
                Generate Pass
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ── Main export ── */
export default function Lounges() {
  const [view, setView] = useState("home"); // "home"|"browse"|"history"|"passes"|"benefit-info"
  const [browseSearch, setBrowseSearch] = useState("");
  const navigate = useNavigate();

  function handleBrowse(query) { setBrowseSearch(query || ""); setView("browse"); }
  function handleMenu(id) { setView(id); }
  function handleBack() { setView("home"); }

  if (view === "browse") return <div className="lounges"><LoungeBrowse initialSearch={browseSearch} onBack={handleBack} /></div>;
  if (view === "history") return <div className="lounges"><LoungeHistory onBack={handleBack} /></div>;
  if (view === "passes") return <div className="lounges"><LoungePasses onBack={handleBack} /></div>;
  if (view === "benefit-info") return <div className="lounges"><LoungeBenefitInfo onBack={handleBack} /></div>;

  return (
    <div className="lounges">
      <LoungeHome onBrowse={handleBrowse} onMenu={handleMenu} navigate={navigate} />
    </div>
  );
}
